import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  Construction,
  Handshake,
  Person4,
  Print,
  Search,
  TrendingDown,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { GridColDef } from "@mui/x-data-grid";
import dayjs, { Dayjs } from "dayjs";
import CustomDatePicker from "../../../components/ui/customDatePicker/customDatePicker.component";
import CustomDataGrid from "../../../components/ui/customDataGrid/customDataGrid.component";
import EntityReferenceLink from "../../../components/ui/entityReference/entityReferenceLink.component";
import ProfitabilityCharts from "../../../components/settlements/profitabilityCharts/profitabilityCharts.component";
import { useHasPermission } from "../../../contexts/auth";
import axios from "../../../api/axios";
import {
  PROFITABILITY_GROUP_BY_OPTIONS,
  SETTLEMENTS_PROFITABILITY_ENDPOINT,
  SETTLEMENTS_PROFITABILITY_STATS_ENDPOINT,
} from "../../../consts";
import { formatNumber } from "../../../utils";
import {
  ProfitabilityGroupBy,
  ProfitabilityStats,
  ProfitabilityStatsResponse,
} from "../../../types/profitability";

const DEFAULT_GROUP_BY = PROFITABILITY_GROUP_BY_OPTIONS[0].value as ProfitabilityGroupBy;
const UNPROFITABLE_ROW_CLASS = "profitability-row-unprofitable";
// Single source for the loss-making red, shared by the summary cards' trend
// icon and the highlighted grid rows. Theme-derived rather than hardcoded -
// MUI's error.main differs between light and dark mode.
const UNPROFITABLE_COLOR = "error.main";
const COSTLY_ROW_CLASS = "profitability-row-costly";
// Money columns share one alignment/width setup - they all read as figures.
const MONEY_COLUMN: Partial<GridColDef> = {
  flex: 1,
  minWidth: 130,
  align: "right",
  headerAlign: "right",
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const isUnprofitable = (row: any) => toNumber(row?.profitability_net) < 0;

// Parts used on the period's jobs are reported alongside profitability but are
// deliberately not subtracted from it (see services/profitability.py). When
// they outweigh what the row actually earned, that margin is only nominal -
// worth flagging even on rows that are technically still in the black.
const additionalCostsExceedProfit = (row: any) => {
  const additionalCosts = toNumber(row?.additional_costs);
  // Nothing to flag when no parts were used at all - a zero would otherwise
  // "exceed" any negative profitability and light the row up for no reason.
  return additionalCosts > 0 && additionalCosts > toNumber(row?.profitability_net);
};

/**
 * Summary card pairing a total with how many of those are loss-making, each
 * with its own icon - the layer's own icon for the total, a downward trend for
 * the unprofitable slice.
 */
const ProfitabilityStatCard = ({
  title,
  icon,
  count,
  unprofitableCount,
  color,
}: {
  title: string;
  icon: React.ReactNode;
  count: number | null;
  unprofitableCount: number | null;
  color: string;
}) => {
  const { t } = useTranslation();
  const hasLosses = (unprofitableCount ?? 0) > 0;

  return (
    <Card
      sx={{
        flex: "1 1 250px",
        minWidth: 250,
        borderRadius: "var(--defaultRadius)",
        boxShadow: 3,
      }}
    >
      <CardContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ color, display: "flex", opacity: 0.8 }}>{icon}</Box>
            <Typography variant="h4" component="div" fontWeight="bold">
              {count ?? "-"}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              // Same red as the unprofitable rows in the table below, so the
              // card and the grid read as one signal.
              color: UNPROFITABLE_COLOR,
            }}
            title={t("Unprofitable")}
          >
            <Box sx={{ display: "flex", opacity: hasLosses ? 1 : 0.55 }}>
              <TrendingDown />
            </Box>
            <Typography
              variant="h4"
              component="div"
              fontWeight="bold"
              sx={{ opacity: hasLosses ? 1 : 0.55 }}
            >
              {unprofitableCount ?? "-"}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const SettlementsProfitability = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const canViewProfitability = useHasPermission("settlement.can_view_profitability");
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewContracts = useHasPermission("settlement.view_contract");
  const canViewEquipment = useHasPermission("settlement.view_equipment");

  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(dayjs());
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState<ProfitabilityStats | null>(null);

  // The layer lives in the URL so the toolbar's quick filters drive it, the
  // same way the statistics tab drives its settlement_kind filter.
  const groupBy = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("group_by");
    return (PROFITABILITY_GROUP_BY_OPTIONS.some((option) => option.value === raw)
      ? raw
      : DEFAULT_GROUP_BY) as ProfitabilityGroupBy;
  }, [location.search]);

  const month = selectedMonth ? selectedMonth.format("YYYY-MM") : undefined;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const fetchStats = useCallback(async () => {
    if (!month) {
      setStats(null);
      return;
    }
    try {
      const response = await axios.get(SETTLEMENTS_PROFITABILITY_STATS_ENDPOINT, {
        params: { month, ...(search ? { search } : {}) },
      });
      setStats((response.data as ProfitabilityStatsResponse).stats);
    } catch {
      setStats(null);
    }
  }, [month, search]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const extraParams = useMemo(
    () => ({
      month,
      group_by: groupBy,
      // Only sent when the search box actually has text: extraParams override
      // same-named params, so an always-present `search: undefined` would wipe
      // out the grid's own quick filter (see useCRUD.query).
      ...(search ? { search } : {}),
    }),
    [month, groupBy, search]
  );

  const moneyColumn = useCallback(
    (field: string, headerName: string): GridColDef => ({
      field,
      headerName,
      ...MONEY_COLUMN,
      renderCell: (params) => (
        <Typography variant="body2">{formatNumber(params.value)}</Typography>
      ),
    }),
    []
  );

  const costColumn = useCallback(
    (): GridColDef => ({
      field: "profitability_cost",
      headerName: t("Cost"),
      ...MONEY_COLUMN,
      minWidth: 175,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Typography variant="body2">{formatNumber(params.value)}</Typography>
          {toNumber(params.row.job_count) > 0 && (
            <Chip
              size="small"
              variant="outlined"
              icon={<Construction />}
              label={params.row.job_count}
              title={t("Jobs included in this cost")}
            />
          )}
        </Box>
      ),
    }),
    [t]
  );

  const additionalCostsColumn = useCallback(
    (): GridColDef => ({
      field: "additional_costs",
      headerName: t("Additional costs"),
      ...MONEY_COLUMN,
      minWidth: 150,
      renderCell: (params) =>
        additionalCostsExceedProfit(params.row) ? (
          <Chip
            size="small"
            color="warning"
            variant="filled"
            label={formatNumber(params.value)}
            title={t("Used parts cost more than this row earned")}
          />
        ) : (
          <Typography variant="body2">{formatNumber(params.value)}</Typography>
        ),
    }),
    [t]
  );

  const profitabilityColumn = useCallback(
    (): GridColDef => ({
      field: "profitability_net",
      headerName: t("Profitability (net)"),
      // Ranked by margin, not amount - see PROFITABILITY_SORT_FIELD_OVERRIDES
      // in the backend selectors.
      description: t("Sorted by margin percentage"),
      ...MONEY_COLUMN,
      minWidth: 170,
      renderCell: (params) => {
        const negative = isUnprofitable(params.row);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              fontWeight="bold"
              color={negative ? "error.main" : "text.primary"}
            >
              {formatNumber(params.value)}
            </Typography>
            {params.row.profitability_percent != null && (
              <Chip
                size="small"
                variant="outlined"
                color={negative ? "error" : "success"}
                label={`${formatNumber(params.row.profitability_percent)}%`}
              />
            )}
          </Box>
        );
      },
    }),
    [t]
  );

  /** Entity name/number cell: opens the entity preview, and turns red when the
   * row is loss-making so the offender is identifiable at a glance. */
  const entityColumn = useCallback(
    (
      field: string,
      headerName: string,
      entityType: "equipment" | "contract" | "client",
      idField: string,
      canPreview: boolean,
      minWidth = 180
    ): GridColDef => ({
      field,
      headerName,
      flex: 1,
      minWidth,
      renderCell: (params) => (
        <Box
          sx={{
            fontWeight: isUnprofitable(params.row) ? 700 : 400,
            color: isUnprofitable(params.row) ? "error.main" : undefined,
          }}
        >
          <EntityReferenceLink
            entityType={entityType}
            entityId={params.row[idField]}
            label={params.value ?? "-"}
            canPreview={canPreview}
          />
        </Box>
      ),
    }),
    []
  );

  const columns: GridColDef[] = useMemo(() => {
    const moneyColumns = [
      moneyColumn("profit", t("Revenue (net)")),
      costColumn(),
      profitabilityColumn(),
      additionalCostsColumn(),
    ];

    if (groupBy === "equipment") {
      return [
        entityColumn(
          "equipment_name",
          t("Equipment"),
          "equipment",
          "equipment_id",
          canViewEquipment,
          200
        ),
        { field: "serial", headerName: t("Serial"), flex: 1, minWidth: 140 },
        entityColumn(
          "contract_number",
          t("Contract"),
          "contract",
          "contract_id",
          canViewContracts
        ),
        entityColumn(
          "client_name",
          t("Client"),
          "client",
          "client_id",
          canViewClients
        ),
        ...moneyColumns,
      ];
    }

    if (groupBy === "contract") {
      return [
        entityColumn(
          "contract_number",
          t("Contract"),
          "contract",
          "contract_id",
          canViewContracts
        ),
        entityColumn(
          "client_name",
          t("Client"),
          "client",
          "client_id",
          canViewClients
        ),
        {
          field: "equipment_count",
          headerName: t("Equipment"),
          flex: 1,
          minWidth: 120,
          align: "right",
          headerAlign: "right",
        },
        ...moneyColumns,
      ];
    }

    return [
      entityColumn("client_name", t("Client"), "client", "client_id", canViewClients),
      {
        field: "contract_count",
        headerName: t("Contracts"),
        flex: 1,
        minWidth: 120,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "equipment_count",
        headerName: t("Equipment"),
        flex: 1,
        minWidth: 120,
        align: "right",
        headerAlign: "right",
      },
      ...moneyColumns,
    ];
  }, [
    groupBy,
    t,
    moneyColumn,
    costColumn,
    profitabilityColumn,
    additionalCostsColumn,
    entityColumn,
    canViewEquipment,
    canViewContracts,
    canViewClients,
  ]);

  const gridIdentity = useMemo(
    () =>
      ({
        equipment: {
          icon: <Print />,
          title: t("Profitability per equipment"),
          settingsKey: "SETTLEMENTS_PROFITABILITY_EQUIPMENT",
        },
        contract: {
          icon: <Handshake />,
          title: t("Profitability per contract"),
          settingsKey: "SETTLEMENTS_PROFITABILITY_CONTRACT",
        },
        client: {
          icon: <Person4 />,
          title: t("Profitability per client"),
          settingsKey: "SETTLEMENTS_PROFITABILITY_CLIENT",
        },
      })[groupBy],
    [groupBy, t]
  );

  if (!canViewProfitability) {
    return (
      <Typography color="text.secondary" sx={{ p: 2, mt: 2 }}>
        {t("You do not have permission to view profitability.")}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
        // The page scrolls as a whole: the cards, filters and charts above the
        // grid would otherwise squeeze it into whatever space they left over,
        // and the rows below the fold became unreachable.
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%" }}>
        <ProfitabilityStatCard
          title={t("Equipment under profitability")}
          icon={<Print />}
          count={stats?.equipment_count ?? null}
          unprofitableCount={stats?.unprofitable_equipment_count ?? null}
          color="#0288d1"
        />
        <ProfitabilityStatCard
          title={t("Contracts under profitability")}
          icon={<Handshake />}
          count={stats?.contract_count ?? null}
          unprofitableCount={stats?.unprofitable_contract_count ?? null}
          color="#2e7d32"
        />
        <ProfitabilityStatCard
          title={t("Clients under profitability")}
          icon={<Person4 />}
          count={stats?.client_count ?? null}
          unprofitableCount={stats?.unprofitable_client_count ?? null}
          color="#6d4c41"
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Box sx={{ width: 200 }}>
          <CustomDatePicker
            label={t("Month")}
            value={selectedMonth}
            onChange={(value: Dayjs | null) => setSelectedMonth(value)}
            views={["year", "month"]}
            openTo="month"
            format="YYYY-MM"
            slotProps={{ textField: { fullWidth: true, size: "small" } }}
          />
        </Box>
        <TextField
          size="small"
          label={t("Search by machine serial, client, contract number")}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Below the filters so the one filter row above scopes the charts and
          the grid alike, and above the grid because the charts are the summary
          the table then details. */}
      <ProfitabilityCharts month={month} groupBy={groupBy} search={search} />

      {/* A floor rather than a share of the leftover height: CustomDataGrid
          resolves its own `height: 100%` against this box, and the page - not
          the grid - is what scrolls. */}
      <Box sx={{ flex: "1 0 auto", minHeight: 520, width: "100%" }}>
      <CustomDataGrid
        // Each layer is a different table (different columns and stored
        // profile), so remount rather than reuse the previous layer's grid.
        key={groupBy}
        icon={gridIdentity.icon}
        title={gridIdentity.title}
        url={SETTLEMENTS_PROFITABILITY_ENDPOINT}
        gridSettingsKey={gridIdentity.settingsKey}
        columns={columns}
        extraParams={extraParams}
        initialSortModel={[{ field: "profitability_net", sort: "asc" }]}
        dataGridProps={{
          pageSizeOptions: [10, 25, 50],
          disableColumnFilter: true,
          getRowClassName: (params) => {
            // A loss is the stronger signal, so red wins when both apply.
            if (isUnprofitable(params.row)) {
              return UNPROFITABLE_ROW_CLASS;
            }
            return additionalCostsExceedProfit(params.row) ? COSTLY_ROW_CLASS : "";
          },
          sx: (theme) => ({
            [`& .${UNPROFITABLE_ROW_CLASS}`]: {
              backgroundColor: alpha(theme.palette.error.main, 0.08),
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.14),
              },
            },
            [`& .${COSTLY_ROW_CLASS}`]: {
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              "&:hover": {
                backgroundColor: alpha(theme.palette.warning.main, 0.18),
              },
            },
          }),
        }}
      />
      </Box>
    </Box>
  );
};
